import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock, User, Calendar, Share2, Linkedin, Twitter, Facebook, ExternalLink } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { blogPosts } from '../mockData';
import SEOHead from '../components/SEOHead';

export const BlogDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  
  // Find the blog post by slug
  const post = blogPosts.find(p => p.slug === slug);

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) {
    return (
      <div className="min-h-screen pt-32 pb-24 bg-black flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-4">Article Not Found</h1>
          <p className="text-gray-400 mb-8">The article you're looking for doesn't exist.</p>
          <Link to="/blog">
            <Button className="btn-primary">Back to Blog</Button>
          </Link>
        </div>
      </div>
    );
  }

  // Related posts (same category, exclude current)
  const relatedPosts = blogPosts
    .filter(p => p.category === post.category && p.id !== post.id)
    .slice(0, 3);

  // Generate schema markup for SEO + GEO/AEO (Article + FAQPage in @graph)
  const articleSchema = {
    "@type": "BlogPosting",
    "@id": `https://radiusconsulting.co.za/blog/${post.slug}#article`,
    "headline": post.title,
    "description": post.excerpt,
    "image": post.image,
    "datePublished": post.date,
    "dateModified": post.modifiedDate || post.date,
    "keywords": post.keywords,
    "articleSection": post.category,
    "author": {
      "@type": "Person",
      "name": post.author,
      "jobTitle": post.role
    },
    "publisher": {
      "@type": "Organization",
      "name": "RADIUS CONSULTING",
      "logo": {
        "@type": "ImageObject",
        "url": "https://radiusconsulting.co.za/logo.png"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://radiusconsulting.co.za/blog/${post.slug}`
    }
  };

  const graph = [articleSchema];

  if (post.faq && post.faq.length > 0) {
    graph.push({
      "@type": "FAQPage",
      "@id": `https://radiusconsulting.co.za/blog/${post.slug}#faq`,
      "mainEntity": post.faq.map((item) => ({
        "@type": "Question",
        "name": item.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": item.answer
        }
      }))
    });
  }

  const schema = {
    "@context": "https://schema.org",
    "@graph": graph
  };

  const shareUrl = `https://radiusconsulting.co.za/blog/${post.slug}`;
  const shareTitle = encodeURIComponent(post.title);

  return (
    <>
      <SEOHead
        title={post.seoTitle || post.title}
        description={post.seoDescription || post.excerpt}
        keywords={post.keywords}
        author={post.author}
        publishedTime={post.date}
        modifiedTime={post.modifiedDate}
        article={true}
        image={post.image}
        url={shareUrl}
        schema={schema}
      />

      <div className="blog-detail-page pt-20 bg-black">
        {/* Hero Section */}
        <section className="blog-hero relative">
          <div className="absolute inset-0 hero-grid opacity-20" />
          
          {/* Back Button */}
          <div className="max-w-5xl mx-auto px-6 pt-12 relative z-10">
            <button 
              onClick={() => navigate('/blog')}
              className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors group"
            >
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
              Back to Blog
            </button>
          </div>

          {/* Article Header */}
          <div className="max-w-5xl mx-auto px-6 py-16 relative z-10">
            <div className="mb-6">
              <span className="px-4 py-2 bg-red-600/20 border border-red-600/30 text-red-500 text-sm font-medium rounded-full">
                {post.category}
              </span>
            </div>

            <h1 className="text-5xl md:text-6xl font-bold text-white mb-8 leading-tight">
              {post.title}
            </h1>

            <p className="text-xl text-gray-400 mb-8 leading-relaxed max-w-3xl">
              {post.excerpt}
            </p>

            {/* Meta Info */}
            <div className="flex flex-wrap items-center gap-6 text-gray-400 mb-8">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-red-600 to-red-800 flex items-center justify-center">
                  <User className="w-6 h-6 text-white" />
                </div>
                <div>
                  <div className="text-white font-medium">{post.author}</div>
                  <div className="text-sm text-gray-500">{post.role}</div>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>{new Date(post.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
              </div>
              
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>{post.readTime}</span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-4">
              <span className="text-gray-400 text-sm">Share:</span>
              <a 
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 bg-gray-900 hover:bg-gray-800 rounded-full transition-colors"
              >
                <Linkedin className="w-4 h-4 text-gray-400" />
              </a>
              <a 
                href={`https://twitter.com/intent/tweet?url=${shareUrl}&text=${shareTitle}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 bg-gray-900 hover:bg-gray-800 rounded-full transition-colors"
              >
                <Twitter className="w-4 h-4 text-gray-400" />
              </a>
              <a 
                href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 bg-gray-900 hover:bg-gray-800 rounded-full transition-colors"
              >
                <Facebook className="w-4 h-4 text-gray-400" />
              </a>
            </div>
          </div>

          {/* Featured Image */}
          <div className="max-w-6xl mx-auto px-6 pb-16 relative z-10">
            <div className="relative h-[500px] rounded-2xl overflow-hidden">
              <img 
                src={post.image} 
                alt={post.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
            </div>
          </div>
        </section>

        {/* Article Content */}
        <section className="py-16 bg-gradient-to-b from-black to-gray-900">
          <div className="max-w-4xl mx-auto px-6">
            <article className="prose prose-invert prose-lg max-w-none">
              {/* Content Sections */}
              {post.content.map((section, index) => (
                <div key={index} className="mb-12">
                  {section.type === 'heading' && (
                    <h2 className="text-3xl font-bold text-white mb-6 mt-12">
                      {section.text}
                    </h2>
                  )}
                  
                  {section.type === 'subheading' && (
                    <h3 className="text-2xl font-bold text-white mb-4 mt-8">
                      {section.text}
                    </h3>
                  )}
                  
                  {section.type === 'paragraph' && (
                    <p className="text-gray-300 leading-relaxed mb-6">
                      {section.text}
                    </p>
                  )}
                  
                  {section.type === 'list' && (
                    <ul className="space-y-3 mb-6">
                      {section.items.map((item, i) => (
                        <li key={i} className="text-gray-300 flex items-start gap-3">
                          <span className="text-red-500 mt-1">•</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  
                  {section.type === 'quote' && (
                    <blockquote className="border-l-4 border-red-600 pl-6 py-4 my-8 bg-gray-900/50 rounded-r-lg">
                      <p className="text-xl text-gray-200 italic mb-2">"{section.text}"</p>
                      {section.author && (
                        <cite className="text-gray-400 text-sm not-italic">— {section.author}</cite>
                      )}
                    </blockquote>
                  )}

                  {section.type === 'callout' && (
                    <div className="bg-red-600/10 border border-red-600/20 rounded-xl p-6 my-8">
                      <h4 className="text-xl font-bold text-white mb-3">{section.title}</h4>
                      <p className="text-gray-300">{section.text}</p>
                    </div>
                  )}

                  {section.type === 'link' && (
                    <div className="my-6">
                      <a 
                        href={section.url}
                        className="inline-flex items-center gap-2 text-red-500 hover:text-red-400 transition-colors"
                        target={section.external ? "_blank" : "_self"}
                        rel={section.external ? "noopener noreferrer" : ""}
                      >
                        {section.text}
                        {section.external && <ExternalLink className="w-4 h-4" />}
                      </a>
                    </div>
                  )}
                </div>
              ))}

              {/* Key Takeaways */}
              {post.keyTakeaways && (
                <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 my-12 border border-gray-700">
                  <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                    <span className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center text-sm">✓</span>
                    Key Takeaways
                  </h3>
                  <ul className="space-y-4">
                    {post.keyTakeaways.map((takeaway, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <span className="text-red-500 font-bold mt-1">{i + 1}.</span>
                        <span className="text-gray-200">{takeaway}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* FAQ Section */}
              {post.faq && post.faq.length > 0 && (
                <div className="my-16">
                  <h3 className="text-3xl font-bold text-white mb-8">Frequently Asked Questions</h3>
                  <div className="space-y-6">
                    {post.faq.map((item, i) => (
                      <div key={i} className="bg-gray-900/50 rounded-xl p-6 border border-gray-800">
                        <h4 className="text-xl font-bold text-white mb-3">{item.question}</h4>
                        <p className="text-gray-300 leading-relaxed">{item.answer}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {post.sources && post.sources.length > 0 && (
                <div className="my-16" data-testid="blog-sources">
                  <h3 className="text-3xl font-bold text-white mb-8">Sources</h3>
                  <ul className="space-y-3 list-disc list-outside pl-6">
                    {post.sources.map((s, i) => (
                      <li key={i} data-testid="blog-source-item" className="text-gray-300 leading-relaxed">
                        <a
                          href={s.url}
                          target="_blank"
                          rel="noopener noreferrer nofollow"
                          className="text-red-500 hover:text-red-400 transition-colors underline underline-offset-4 decoration-red-500/40 hover:decoration-red-400"
                        >
                          {s.title}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </article>

            {/* Internal Links to Services/Resources */}
            <div className="grid md:grid-cols-2 gap-6 my-16">
              <Card className="bg-gradient-to-br from-red-600/10 to-red-800/5 border-red-600/20 hover:border-red-600/40 transition-all">
                <CardContent className="p-8">
                  <h4 className="text-xl font-bold text-white mb-3">Explore Our Services</h4>
                  <p className="text-gray-400 mb-4">See how we can help transform your brand strategy.</p>
                  <Link to="/services">
                    <Button variant="outline" className="group">
                      View Services
                      <ArrowLeft className="ml-2 w-4 h-4 rotate-180 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-gray-900 to-gray-800 border-gray-700 hover:border-gray-600 transition-all">
                <CardContent className="p-8">
                  <h4 className="text-xl font-bold text-white mb-3">Free Resources</h4>
                  <p className="text-gray-400 mb-4">Download our guides and templates.</p>
                  <Link to="/resources">
                    <Button variant="outline" className="group">
                      Browse Resources
                      <ArrowLeft className="ml-2 w-4 h-4 rotate-180 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </div>

            {/* Author Bio */}
            <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl p-8 my-12 border border-gray-800">
              <div className="flex items-start gap-6">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-red-600 to-red-800 flex items-center justify-center flex-shrink-0">
                  <User className="w-10 h-10 text-white" />
                </div>
                <div>
                  <h4 className="text-xl font-bold text-white mb-2">About {post.author}</h4>
                  <p className="text-gray-400 mb-4">{post.authorBio}</p>
                  <Link to="/about" className="text-red-500 hover:text-red-400 transition-colors">
                    Learn more about our team →
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Related Articles */}
        {relatedPosts.length > 0 && (
          <section className="py-24 bg-black">
            <div className="max-w-7xl mx-auto px-6">
              <h3 className="text-4xl font-bold text-white mb-12">Related Articles</h3>
              
              <div className="grid md:grid-cols-3 gap-8">
                {relatedPosts.map((relatedPost) => (
                  <Link key={relatedPost.id} to={`/blog/${relatedPost.slug}`}>
                    <Card className="blog-card group overflow-hidden hover-lift h-full">
                      <div className="relative h-56 overflow-hidden">
                        <img 
                          src={relatedPost.image} 
                          alt={relatedPost.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                        <div className="absolute top-4 left-4">
                          <span className="px-3 py-1 bg-red-600/90 text-white text-xs font-medium rounded-full">
                            {relatedPost.category}
                          </span>
                        </div>
                      </div>
                      
                      <CardContent className="p-6">
                        <h4 className="text-xl font-bold text-white mb-3 group-hover:text-gradient transition-all line-clamp-2">
                          {relatedPost.title}
                        </h4>
                        <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                          {relatedPost.excerpt}
                        </p>
                        
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {relatedPost.readTime}
                          </span>
                          <span className="text-red-500 group-hover:translate-x-1 transition-transform">→</span>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* CTA Section */}
        <section className="py-24 bg-gradient-to-br from-red-600/10 to-transparent">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h3 className="text-5xl font-bold text-white mb-6">
              Ready to Talk to Radius Consulting?
            </h3>
            <p className="text-xl text-gray-400 mb-12">
              Let's discuss how we can help you achieve your marketing goals.
            </p>
            <Link to="/contact">
              <Button size="lg" className="btn-primary group" data-testid="blog-cta-talk-to-radius">
                Talk to Radius Consulting
                <ArrowLeft className="ml-2 w-5 h-5 rotate-180 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </section>
      </div>
    </>
  );
};

export default BlogDetail;
